import React from 'react';

export interface SubTab {
  id: string;
  label: string;
  content: React.ReactNode;
}

export interface Tab {
  id: string;
  label: React.ReactNode;
  subTabs?: SubTab[];
  content?: React.ReactNode;
  friendlyName: string;
}

export interface TabControlProps {
  tabs: Tab[];
  defaultActiveTabId?: string;
}

export const TabControl: React.FC<TabControlProps> = ({
  tabs,
  defaultActiveTabId,
}) => {
  const initialTabIndex = defaultActiveTabId
    ? tabs.findIndex((tab) => tab.id === defaultActiveTabId)
    : 0;

  const [activeTabIndex, setActiveTabIndex] = React.useState(
    initialTabIndex >= 0 ? initialTabIndex : 0
  );

  const [activeSubTab, setActiveSubTab] = React.useState<string | null>(
    tabs[activeTabIndex]?.subTabs?.[0]?.id || null
  );

  const handleTabClick = (tabIndex: number) => {
    setActiveTabIndex(tabIndex);
    setActiveSubTab(tabs[tabIndex]?.subTabs?.[0]?.id || null);
  };

  const handleSubTabClick = (subTabId: string) => {
    setActiveSubTab(subTabId);
  };

  const activeTabData = tabs[activeTabIndex];
  const activeSubTabData = activeTabData?.subTabs?.find(
    (subTab) => subTab.id === activeSubTab
  );

  return (
    <div className="w-full p-6 bg-white dark:bg-black rounded-lg shadow-lg">
      <h2 className="text-xl font-semibold text-gray-400 dark:text-gray-500 mb-4">
        Minha Conta
      </h2>

      {/* Abas - Dropdown no mobile e tabs no desktop */}
      <div className="mb-0">
        {/* Dropdown no mobile */}
        <div className="md:hidden">
          <select
            value={tabs[activeTabIndex]?.id}
            onChange={(e) => {
              const tabIndex = tabs.findIndex((tab) => tab.id === e.target.value);
              handleTabClick(tabIndex);
            }}
            className="block w-full border border-gray-300 rounded-md p-2"
          >
            {tabs.map((tab) => (
              <option key={tab.id} value={tab.id}>
                {typeof tab.label === 'string' ? tab.label : `${tab.friendlyName}`}
              </option>
            ))}
          </select>
        </div>

        {/* Tabs no desktop */}
        <div className="hidden md:flex border-b border-gray-200 dark:border-gray">
          {tabs.map((tab, index) => (
            <button
              key={tab.id}
              onClick={() => handleTabClick(index)}
              className={`px-4 py-2 font-medium text-sm transition-colors duration-200 ${
                activeTabIndex === index
                  ? 'border-b-2 border-[#1F467E] text-[#1F467E]'
                  : 'text-gray-600 dark:text-black hover:text-[#1F467E]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Sub-Abas */}
      {activeTabData?.subTabs && (
        <div className="flex flex-wrap border-b border-gray-200 dark:border-gray mb-4">
          {activeTabData.subTabs.map((subTab) => (
            <button
              key={subTab.id}
              onClick={() => handleSubTabClick(subTab.id)}
              className={`px-3 py-1 mr-2 mb-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                activeSubTab === subTab.id
                  ? 'bg-[#1F467E] text-white'
                  : 'bg-gray-100 dark:bg-black text-gray-600 dark:text-black hover:bg-[#1F467E] hover:text-white'
              }`}
            >
              {subTab.label}
            </button>
          ))}
        </div>
      )}

      {/* Conteúdo */}
      <div className="bg-gray-50 dark:bg-white rounded-md min-h-[200px] p-2">
        {activeSubTabData ? (
          activeSubTabData.content
        ) : activeTabData?.content ? (
          activeTabData.content
        ) : (
          <p className="text-gray-500 dark:text-gray-400">
            Selecione uma aba para visualizar o conteúdo.
          </p>
        )}
      </div>
    </div>
  );
};
